import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Autocomplete,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { LIST } from "../config/const";
// config
import { TableColumn, DataColumn } from "../config/dataInterface";
// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

const CustomTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    fontSize: "12px",
    height: "12px", // ここでフォントサイズを指定
  },
});
CustomTextField.defaultProps = {
  autoComplete: "off",
};
function SerchInputField({
  inputList,
  label,
  marginLeft,
  width,
  value,
  setValue,
  marginTop,
  disabled,
  onKeyDown,
  popperContainerRef,
  popperContainer,
  embeddedInModal,
}: {
  inputList: string[];
  label: string;
  marginLeft: string;
  width: string;
  value: string;
  setValue: (value: string) => void;
  marginTop: string;
  disabled: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  popperContainerRef?: React.RefObject<HTMLElement | null>;
  popperContainer?: HTMLElement | null;
  embeddedInModal?: boolean;
}) {
  const container = popperContainer ?? popperContainerRef?.current;
  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: marginTop,
        marginBottom: "20px",
        marginLeft,
        width,
      }}
    >
      <Autocomplete
        disabled={disabled}
        clearIcon={null}
        options={inputList}
        value={value}
        slotProps={{
          popper: {
            ...(container ? { container } : {}),
            ...(embeddedInModal ? { sx: { zIndex: 1500 } } : {}),
          },
        }}
        onChange={(event: any, newValue: string | null) => {
          if (newValue === null) {
            setValue("");
          } else {
            setValue(newValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            value={value}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "12px",
                height: "32px", // ここでフォントサイズを指定
              },
            }}
            {...params}
            onKeyDown={onKeyDown}
          />
        )}
      />
    </FormControl>
  );
}

// 複数選択対応フィールドのキー一覧
const MULTI_SELECT_KEYS = new Set([
  // Contract
  "category_name",
  "high_rank_company_name",
  "product_name",
  "sale_company_name",
  "sale_company_group_name",
  "toss_up_company_name",
  "toss_up_account_group_name",
  "toss_up_account_name",
  "follow_call_status",
  "sale_account_name",
  // TossUp
  "toss_up_status",
  "category_name_list",
  "product_name_list",
  "calling_account_name",
  "toss_up_company_group_name",
  "closer_company_name",
  "closer_account_name",
  "closer_product_name",
  // Service (明細管理) / FollowCall / Incentive / OutsideEntryForm
  "entry_sale_company_name",
  "payment_sale_company_name",
  // PaymentConfirm (確定済明細一覧)
  "staff_account_name",
  // ScreenColumnConfig (システム項目設定)
  "login_sale_company",
  // SaleCompany (パートナー管理)
  "group_name",
  "toss_up_product_name",
  "entry_product_name",
  "collaborate_company_name",
  // EntryAnalysys 表示区分（複数選択）
  "entry_analysys_division_admin",
  "entry_analysys_division_partner",
  // TossUpAnalysys / ClosingAnalysys 表示区分（複数選択）
  "toss_up_analysys_division_admin",
  "toss_up_analysys_division_partner",
]);

function SerchMultiInputField({
  inputList,
  value,
  setValue,
  disabled,
  popperContainerRef,
  popperContainer,
  embeddedInModal,
}: {
  inputList: string[];
  value: string;
  setValue: (value: string) => void;
  disabled: boolean;
  popperContainerRef?: React.RefObject<HTMLElement | null>;
  popperContainer?: HTMLElement | null;
  embeddedInModal?: boolean;
}) {
  const container = popperContainer ?? popperContainerRef?.current;
  const selectedValues = value ? value.split(",").filter((v) => v !== "") : [];
  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: "10px",
        marginBottom: "20px",
        marginLeft: "5px",
        width: "calc(100% - 10px)",
      }}
    >
      <Autocomplete
        multiple
        disabled={disabled}
        options={inputList}
        value={selectedValues}
        slotProps={{
          popper: {
            ...(container ? { container } : {}),
            ...(embeddedInModal ? { sx: { zIndex: 1500 } } : {}),
          },
        }}
        onChange={(_event: any, newValues: string[]) => {
          setValue(newValues.join(","));
        }}
        renderInput={(params) => (
          <TextField
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "12px",
              },
            }}
            {...params}
          />
        )}
        renderTags={(tagValues: string[], getTagProps) =>
          tagValues.map((option: string, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                {...tagProps}
                size="small"
                sx={{ fontSize: "11px" }}
              />
            );
          })
        }
      />
    </FormControl>
  );
}

function SerchTextField({
  label,
  helperText,
  value,
  setValue,
  onKeyDown,
}: {
  label: string;
  helperText: string;
  value: string;
  setValue: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const textFieldMargin = "10px 5px ";
  return (
    <Box>
      <Box className="serch-label">{label}</Box>
      <CustomTextField
        id="textFieldId"
        value={value}
        size="small"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        sx={{ width: "calc(100% - 10px)", margin: textFieldMargin }}
      />
      <FormHelperText
        id="serch-name"
        sx={{
          width: "calc(100% - 10px)",
          textAlign: "right",
          marginTop: "-8px",
        }}
      >
        {helperText}
      </FormHelperText>
    </Box>
  );
}

function SerchDateField({
  label,
  valueStart,
  setValueStart,
  valueEnd,
  setValueEnd,
  onKeyDown,
}: {
  label: string;
  valueStart: string;
  setValueStart: (value: string) => void;
  valueEnd: string;
  setValueEnd: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const textFieldMargin = "10px 5px ";
  return (
    <Box>
      <Box className="serch-label">{label}</Box>
      <CustomTextField
        type="date"
        id="textFieldId"
        value={valueStart}
        size="small"
        onChange={(e) => setValueStart(e.target.value)}
        onKeyDown={onKeyDown}
        sx={{ width: "calc(100% - 10px)", margin: "8px 4px 8px" }}
      />
      <Box style={{ display: "flex" }}>
        <Box style={{ marginLeft: "5px", width: "10px" }}>~</Box>
        <CustomTextField
          type="date"
          id="textFieldId"
          value={valueEnd}
          size="small"
          onChange={(e) => setValueEnd(e.target.value)}
          onKeyDown={onKeyDown}
          sx={{ width: "calc(100% - 25px)", margin: "0px 4px 20px" }}
        />
      </Box>
    </Box>
  );
}

interface SerchSideBarProps {
  setSerchOpen: (open: boolean) => void;
  tableHeight: string;
  tableColumn: TableColumn;
  dataColumn: DataColumn[];
  firstYear: number;
  thisYear: number;
  serchColumn: { [key: string]: string };
  setSerchString: (value: { [key: string]: string }) => void;
  companyNameList: string[];
  companyGroupNameList: string[];
  highRankCompanyNameList: string[];
  categoryNameList: string[];
  productNameList: string[];
  productCandidateMap?: { [key: string]: string[] };
  accountSaleNameList: string[];
  accountStaffNameList: string[];
  serchStringView: { [key: string]: string };
  setSerchStringView: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  /** モーダル内でドロップダウンを表示する際に指定（例: 詳細モーダル） */
  popperContainerRef?: React.RefObject<HTMLElement | null>;
  /** モーダル内で Popper を描画するコンテナ要素（ref は初回 null になり得るため、親で state 保持して渡す） */
  popperContainer?: HTMLElement | null;
  /** トスアップ分析 / クロージング分析で表示するリストを切り替える */
  analysisSubType?: "tossup" | "closing";
  /** モーダル内に埋め込む場合。高さを親に合わせ、検索ボタンを常に表示する */
  embeddedInModal?: boolean;
  /** ランキングページの表示範囲設定（日別/月別） */
  displayMode?: "日別 / 月別" | "日別" | "月別";
  setDisplayMode?: (mode: "日別 / 月別" | "日別" | "月別") => void;
  displayModeList?: string[];
}

function SerchSideBar(props: SerchSideBarProps) {
  const {
    setSerchOpen,
    tableHeight,
    tableColumn,
    dataColumn,
    firstYear,
    thisYear,
    serchColumn,
    setSerchString,
    companyNameList,
    companyGroupNameList,
    highRankCompanyNameList,
    categoryNameList,
    productNameList,
    productCandidateMap,
    accountSaleNameList,
    accountStaffNameList,
    serchStringView,
    setSerchStringView,
    popperContainerRef,
    popperContainer,
    analysisSubType,
    embeddedInModal = false,
    displayMode,
    setDisplayMode,
    displayModeList,
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);

  const getTossUpListKey = (key: string): string => {
    if (analysisSubType === "tossup") {
      if (key === "toss_up_analysys_division_admin")
        return "toss_up_analysys_division_admin_tossup";
      if (key === "toss_up_analysys_division_partner")
        return "toss_up_analysys_division_partner_toss_up";
      if (key === "toss_up_analysys_count_type")
        return "toss_up_analysys_count_type_toss_up";
      if (key === "toss_up_aggregation_item")
        return "toss_up_aggregation_item_tossup";
      if (key === "toss_up_analysys_sub_count")
        return "toss_up_analysys_sub_count_tossup";
    }
    if (analysisSubType === "closing") {
      if (key === "toss_up_analysys_division_admin")
        return "toss_up_analysys_division_admin_closing";
      if (key === "toss_up_analysys_division_partner")
        return "toss_up_analysys_division_partner_closing";
      if (key === "toss_up_analysys_count_type")
        return "toss_up_analysys_count_type_closing";
      if (key === "toss_up_aggregation_item")
        return "toss_up_aggregation_item_closing";
      if (key === "toss_up_analysys_sub_count")
        return "toss_up_analysys_sub_count_closing";
    }
    return key;
  };

  /** 集計項目表示 → entry_analysys_count_type */
  const entryAggregationToCountType = (agg: string): string => {
    if (agg === "エントリー数") return "システム登録日";
    if (agg === "開通数") return "開通日";
    if (agg === "解約数") return "解約日";
    return "システム登録日";
  };
  /** entry_analysys_count_type → 集計項目表示 */
  const entryCountTypeToAggregation = (ct: string): string => {
    if (ct === "システム登録日") return "エントリー数";
    if (ct === "開通日") return "開通数";
    if (ct === "解約日") return "解約数";
    return "エントリー数";
  };
  const pathname = useAtomValue(AppAtoms.pathname);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const createMonthList = () => {
    let monthList = [];
    for (let i = 1; i <= 12; i++) {
      monthList.push(i.toString());
    }
    return monthList;
  };
  const createYearList = () => {
    let yearList = [];
    for (let i = firstYear; i <= thisYear; i++) {
      yearList.push(i.toString());
    }
    return yearList;
  };

  const [screenDataColumn, setScreenDataColumn] = useState<DataColumn[]>([]);
  useEffect(() => {
    setScreenDataColumn(
      dataColumn.length > 0 ? dataColumn : tableColumn.dataColumn,
    );
  }, [tableColumn, dataColumn]);

  // エンターキーで検索を実行するハンドラー
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSerchString(serchStringView);
      if (isSmartPhone) {
        setSerchOpen(false);
      }
    }
  };
  return (
    <Box
      sx={{
        minWidth:
          isSmartPhone && !embeddedInModal ? "calc(100% + 30px)" : "300px",
        maxWidth: isSmartPhone && !embeddedInModal ? undefined : "300px",
        marginTop: isSmartPhone && !embeddedInModal ? "80px" : undefined,
        height: embeddedInModal
          ? "100%"
          : isSmartPhone
            ? "auto"
            : "calc(100vh - 80px)",
        borderLeft:
          isSmartPhone && !embeddedInModal ? undefined : "silver 0.2px solid",
        position:
          isSmartPhone && !embeddedInModal
            ? undefined
            : embeddedInModal
              ? undefined
              : "absolute",
        right: isSmartPhone && !embeddedInModal ? undefined : 0,
        display: embeddedInModal ? "flex" : "block",
        flexDirection: "column",
        overflow: embeddedInModal || !isSmartPhone ? "hidden" : "visible",
      }}
    >
      <Box
        sx={{
          margin: embeddedInModal ? "0 10px" : "15px 10px",
          flex: embeddedInModal ? "1 1 0" : undefined,
          minHeight: embeddedInModal ? 0 : undefined,
          display: "flex",
          flexDirection: "column",
          height: embeddedInModal
            ? "100%"
            : isSmartPhone
              ? undefined
              : "calc(100% - 25px)",
        }}
      >
        <Box
          sx={{
            height: "25px",
            flexShrink: 0,
          }}
          key="serchTitleBox"
          className="shippori-antique-regular"
        >
          検索
        </Box>
        <Box
          sx={{
            flex: embeddedInModal || !isSmartPhone ? "1 1 0" : undefined,
            minHeight: embeddedInModal || !isSmartPhone ? 0 : undefined,
            overflowY: embeddedInModal || !isSmartPhone ? "auto" : undefined,
            marginBottom: "10px",
            marginTop: "10px",
          }}
        >
          {displayMode !== undefined && setDisplayMode && (
            <Box sx={{ mb: 1.5 }}>
              <Box className="serch-label">表示範囲設定</Box>
              <FormControl
                size="small"
                sx={{ width: "calc(100% - 10px)", margin: "8px 4px 8px" }}
              >
                <Select
                  value={displayMode}
                  onChange={(e) =>
                    setDisplayMode(
                      e.target.value as "日別 / 月別" | "日別" | "月別",
                    )
                  }
                  sx={{ fontSize: "14px" }}
                >
                  {(displayModeList ?? LIST["display_mode"]).map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {screenDataColumn.map((dataColumn, i) => {
            if (dataColumn.serch === true) {
              if (
                loginUser &&
                dataColumn.tableDetailAuth.includes(loginUser.authority)
              ) {
                if (dataColumn.serchType === "bubun") {
                  return (
                    <SerchTextField
                      key={"serchColumnBubun" + i}
                      label={dataColumn.name}
                      helperText="※部分一致"
                      value={serchStringView[dataColumn.key]}
                      setValue={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key]: value,
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  );
                } else if (dataColumn.serchType === "kanzen") {
                  return (
                    <SerchTextField
                      key={"serchColumnKanzen" + i}
                      label={dataColumn.name}
                      helperText="※完全一致"
                      value={serchStringView[dataColumn.key]}
                      setValue={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key]: value,
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  );
                } else if (dataColumn.serchType === "date") {
                  return (
                    <SerchDateField
                      key={"serchColumnDate" + i}
                      label={dataColumn.name}
                      valueStart={serchStringView[dataColumn.key + "_start"]}
                      setValueStart={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key + "_start"]: value,
                        }));
                      }}
                      valueEnd={serchStringView[dataColumn.key + "_end"]}
                      setValueEnd={(value) => {
                        setSerchStringView((serchStringView) => ({
                          ...serchStringView,
                          [dataColumn.key + "_end"]: value,
                        }));
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  );
                } else if (dataColumn.serchType === "analysys_year_month") {
                  if (serchStringView.analysys_granularity == "日別") {
                    return (
                      <Box>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchInputField
                          key={"serchEndYearColumn" + i}
                          inputList={createYearList()}
                          label="年"
                          marginLeft="5px"
                          width="100px"
                          value={serchStringView.analysys_granularity_date_year}
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_date_year: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        <SerchInputField
                          key={"serchEndMonthColumn" + i}
                          inputList={createMonthList()}
                          label="月"
                          marginLeft="10px"
                          width="75px"
                          value={
                            serchStringView.analysys_granularity_date_month
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_date_month: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  } else if (serchStringView.analysys_granularity == "月別") {
                    return (
                      <Box>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchInputField
                          key={"serchEndYearColumn" + i}
                          inputList={createYearList()}
                          label="年"
                          marginLeft="5px"
                          width="100px"
                          value={
                            serchStringView.analysys_granularity_month_start_year
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_month_start_year: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        <SerchInputField
                          key={"serchEndMonthColumn" + i}
                          inputList={createMonthList()}
                          label="月"
                          marginLeft="10px"
                          width="75px"
                          value={
                            serchStringView.analysys_granularity_month_start_month
                          }
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              analysys_granularity_month_start_month: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                        <Box style={{ display: "flex" }}>
                          <Box
                            style={{
                              marginLeft: "45px",
                              width: "10px",
                              marginTop: "-5px",
                            }}
                          >
                            ~
                          </Box>
                          <SerchInputField
                            key={"serchEndYearColumn" + i}
                            inputList={createYearList()}
                            label="年"
                            marginLeft="15px"
                            width="100px"
                            value={
                              serchStringView.analysys_granularity_month_end_year
                            }
                            setValue={(value) => {
                              setSerchStringView((serchStringView) => ({
                                ...serchStringView,
                                analysys_granularity_month_end_year: value,
                              }));
                            }}
                            marginTop="-5px"
                            disabled={false}
                            onKeyDown={handleKeyDown}
                            popperContainerRef={popperContainerRef}
                            popperContainer={popperContainer}
                            embeddedInModal={embeddedInModal}
                          />
                          <SerchInputField
                            key={"serchEndMonthColumn" + i}
                            inputList={createMonthList()}
                            label="月"
                            marginLeft="10px"
                            width="75px"
                            value={
                              serchStringView.analysys_granularity_month_end_month
                            }
                            setValue={(value) => {
                              setSerchStringView((serchStringView) => ({
                                ...serchStringView,
                                analysys_granularity_month_end_month: value,
                              }));
                            }}
                            marginTop="-5px"
                            disabled={false}
                            onKeyDown={handleKeyDown}
                          />
                        </Box>
                      </Box>
                    );
                  }
                } else if (dataColumn.serchType === "runking_day") {
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <CustomTextField
                        type="date"
                        id="textFieldId"
                        value={serchStringView.runking_granularity_date}
                        size="small"
                        onChange={(e) =>
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            runking_granularity_date: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                        sx={{
                          width: "calc(100% - 10px)",
                          margin: "8px 4px 8px",
                        }}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "payment_year_month") {
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchEndYearColumn" + i}
                        inputList={createYearList()}
                        label="年"
                        marginLeft="5px"
                        width="100px"
                        value={serchStringView.payment_year}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            payment_year: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                      <SerchInputField
                        key={"serchEndMonthColumn" + i}
                        inputList={createMonthList()}
                        label="月"
                        marginLeft="10px"
                        width="75px"
                        value={serchStringView.payment_month}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            payment_month: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "list") {
                  const isEntryAggregation =
                    dataColumn.key === "entry_aggregation_item";
                  const listLabel = dataColumn.name;
                  const inputList = isEntryAggregation
                    ? LIST["entry_aggregation_item"]
                    : analysisSubType
                      ? dataColumn.key ===
                          "toss_up_analysys_division_partner" &&
                        loginUser?.db_authority === "トスアップアカウント"
                        ? LIST[
                            "toss_up_analysys_division_partner_toss_up_account"
                          ]
                        : LIST[getTossUpListKey(dataColumn.key)]
                      : dataColumn.key === "toss_up_analysys_count_type" &&
                          loginUser?.db_authority === "トスアップアカウント"
                        ? LIST["toss_up_analysys_count_type_toss_up"]
                        : LIST[dataColumn.key];
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnListMulti" + i}>
                        <Box className="serch-label">{listLabel}</Box>
                        <SerchMultiInputField
                          inputList={inputList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  const displayValue = isEntryAggregation
                    ? entryCountTypeToAggregation(
                        serchStringView.entry_analysys_count_type ?? "",
                      )
                    : serchStringView[dataColumn.key];
                  return (
                    <Box>
                      <Box className="serch-label">{listLabel}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={inputList}
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={displayValue}
                        setValue={(value) => {
                          if (isEntryAggregation) {
                            setSerchStringView((prev) => ({
                              ...prev,
                              entry_aggregation_item: value,
                              entry_analysys_count_type:
                                entryAggregationToCountType(value),
                            }));
                          } else if (
                            dataColumn.key === "entry_analysys_count_type" &&
                            value === "売上"
                          ) {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              [dataColumn.key]: value,
                              analysys_granularity: "月別",
                            }));
                          } else {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              [dataColumn.key]: value,
                            }));
                          }
                        }}
                        marginTop="10px"
                        disabled={
                          analysisSubType
                            ? false
                            : dataColumn.key ===
                                  "toss_up_analysys_division_partner" &&
                                loginUser?.db_authority ===
                                  "トスアップアカウント"
                              ? true
                              : dataColumn.key ===
                                    "toss_up_analysys_count_type" &&
                                  loginUser?.db_authority ===
                                    "トスアップアカウント"
                                ? true
                                : dataColumn.key === "analysys_granularity" &&
                                    serchStringView.entry_analysys_count_type ===
                                      "売上"
                                  ? true
                                  : false
                        }
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "company") {
                  if (
                    loginUser?.authority === "マスターアカウント" ||
                    loginUser?.authority === "スタッフアカウント" ||
                    ((loginUser?.authority === "パートナー" ||
                      loginUser?.authority === "営業アカウント") &&
                      (pathname == "/toss_up" || pathname == "/contract"))
                  ) {
                    if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                      return (
                        <Box key={"serchColumnCompanyMulti" + i}>
                          <Box className="serch-label">{dataColumn.name}</Box>
                          <SerchMultiInputField
                            inputList={companyNameList}
                            value={serchStringView[dataColumn.key] || ""}
                            setValue={(value) => {
                              setSerchStringView((prev) => ({
                                ...prev,
                                [dataColumn.key]: value,
                              }));
                            }}
                            disabled={false}
                            popperContainerRef={popperContainerRef}
                            popperContainer={popperContainer}
                            embeddedInModal={embeddedInModal}
                          />
                        </Box>
                      );
                    }
                    return (
                      <Box>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchInputField
                          key={"serchColumnList" + i}
                          inputList={companyNameList}
                          label=""
                          marginLeft="5px"
                          width="calc(100% - 10px)"
                          value={serchStringView[dataColumn.key]}
                          setValue={(value) => {
                            setSerchStringView((serchStringView) => ({
                              ...serchStringView,
                              [dataColumn.key]: value,
                            }));
                          }}
                          marginTop="10px"
                          disabled={false}
                          onKeyDown={handleKeyDown}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                } else if (dataColumn.serchType === "group") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnGroupMulti" + i}>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchMultiInputField
                          inputList={companyGroupNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={companyGroupNameList}
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "high_rank_company") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnHighRankMulti" + i}>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchMultiInputField
                          inputList={highRankCompanyNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={highRankCompanyNameList}
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "category") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnCategoryMulti" + i}>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchMultiInputField
                          inputList={categoryNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={categoryNameList}
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "product") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnProductMulti" + i}>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchMultiInputField
                          inputList={
                            (productCandidateMap &&
                              productCandidateMap[dataColumn.key]) ??
                            productNameList
                          }
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={
                          (productCandidateMap &&
                            productCandidateMap[dataColumn.key]) ??
                          productNameList
                        }
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "staff_account") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnStaffMulti" + i}>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchMultiInputField
                          inputList={accountStaffNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={accountStaffNameList}
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                } else if (dataColumn.serchType === "product_partner_month") {
                  const isDaily = displayMode === "日別";
                  return (
                    <Box key={"serchColumnProductPartnerMonth" + i}>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      {!isDaily ? (
                        <Box>
                          <CustomTextField
                            type="month"
                            size="small"
                            value={serchStringView["start_month"] || ""}
                            onChange={(e) =>
                              setSerchStringView((prev) => ({
                                ...prev,
                                start_month: e.target.value,
                              }))
                            }
                            onKeyDown={handleKeyDown}
                            sx={{
                              width: "calc(100% - 10px)",
                              margin: "8px 4px 8px",
                            }}
                          />
                          <Box style={{ display: "flex" }}>
                            <Box style={{ marginLeft: "5px", width: "10px" }}>
                              ~
                            </Box>
                            <CustomTextField
                              type="month"
                              size="small"
                              value={serchStringView["end_month"] || ""}
                              onChange={(e) =>
                                setSerchStringView((prev) => ({
                                  ...prev,
                                  end_month: e.target.value,
                                }))
                              }
                              onKeyDown={handleKeyDown}
                              sx={{
                                width: "calc(100% - 25px)",
                                margin: "0px 4px 20px",
                              }}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <CustomTextField
                          type="month"
                          size="small"
                          value={serchStringView["year_month"] || ""}
                          onChange={(e) =>
                            setSerchStringView((prev) => ({
                              ...prev,
                              year_month: e.target.value,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                          sx={{
                            width: "calc(100% - 10px)",
                            margin: "8px 4px 8px",
                          }}
                        />
                      )}
                    </Box>
                  );
                } else if (dataColumn.serchType === "sale_account") {
                  if (MULTI_SELECT_KEYS.has(dataColumn.key)) {
                    return (
                      <Box key={"serchColumnSaleAccountMulti" + i}>
                        <Box className="serch-label">{dataColumn.name}</Box>
                        <SerchMultiInputField
                          inputList={accountSaleNameList}
                          value={serchStringView[dataColumn.key] || ""}
                          setValue={(value) => {
                            setSerchStringView((prev) => ({
                              ...prev,
                              [dataColumn.key]: value,
                            }));
                          }}
                          disabled={false}
                          popperContainerRef={popperContainerRef}
                          popperContainer={popperContainer}
                          embeddedInModal={embeddedInModal}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box className="serch-label">{dataColumn.name}</Box>
                      <SerchInputField
                        key={"serchColumnList" + i}
                        inputList={accountSaleNameList}
                        label=""
                        marginLeft="5px"
                        width="calc(100% - 10px)"
                        value={serchStringView[dataColumn.key]}
                        setValue={(value) => {
                          setSerchStringView((serchStringView) => ({
                            ...serchStringView,
                            [dataColumn.key]: value,
                          }));
                        }}
                        marginTop="10px"
                        disabled={false}
                        onKeyDown={handleKeyDown}
                        popperContainerRef={popperContainerRef}
                        popperContainer={popperContainer}
                        embeddedInModal={embeddedInModal}
                      />
                    </Box>
                  );
                }
              }
            }
          })}
        </Box>
        {isSmartPhone && !embeddedInModal ? (
          <Box sx={{ height: "60px" }} />
        ) : null}
        <Box
          sx={{
            position: isSmartPhone && !embeddedInModal ? "fixed" : undefined,
            bottom: isSmartPhone && !embeddedInModal ? 0 : undefined,
            left: isSmartPhone && !embeddedInModal ? 0 : undefined,
            backgroundColor:
              isSmartPhone && !embeddedInModal ? "white" : undefined,
            width: isSmartPhone && !embeddedInModal ? "100vw" : undefined,
            height: isSmartPhone && !embeddedInModal ? "60px" : "50px",
            borderTop: "silver 0.2px solid",
            flexShrink: 0,
          }}
        >
          <Box key="serchBottunBox" style={{ display: "flex" }}>
            {!embeddedInModal && (
              <Button
                variant="contained"
                style={{
                  height: "40px",
                  width: isSmartPhone ? "calc(50% - 15px)" : "calc(50% - 5px)",
                  marginTop: "10px",
                  marginLeft: isSmartPhone ? "10px" : undefined,
                  textTransform: "none",
                }}
                onClick={async () => {
                  setSerchString(serchColumn);
                  setSerchStringView(serchColumn);
                  if (isSmartPhone) {
                    setSerchOpen(false);
                  }
                }}
              >
                <Box className="m-plus-rounded-1c-regular">検索クリア</Box>
              </Button>
            )}
            <Button
              variant="contained"
              style={{
                height: "40px",
                width: embeddedInModal
                  ? "calc(100% - 10px)"
                  : isSmartPhone
                    ? "calc(50% - 15px)"
                    : "calc(50% - 5px)",
                marginTop: "10px",
                marginLeft: embeddedInModal
                  ? "5px"
                  : isSmartPhone
                    ? "5px"
                    : "10px",
                textTransform: "none",
              }}
              onClick={async () => {
                setSerchString(serchStringView);
                if (isSmartPhone) {
                  setSerchOpen(false);
                }
              }}
            >
              <Box className="m-plus-rounded-1c-regular">検索</Box>
              <SearchIcon
                sx={{ marginLeft: "6px", color: "white" }}
                fontSize="medium"
              />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SerchSideBar;
